// Zayna Home — stable global interaction controllers (v5 rollback).
(() => {
  'use strict';

  document.documentElement.classList.add('js');

  // Mark this version explicitly. Do not reuse the old guard: this patch must
  // initialise even when a stale controller set the former global flag.
  window.__zhThemeInit = true;
  window.__zhThemeVersion = '5';

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const visibleFocusables = (container) => Array.from(
    container?.querySelectorAll(focusableSelector) || []
  ).filter((element) => (
    !element.hidden &&
    !element.closest('[hidden]') &&
    element.getAttribute('aria-hidden') !== 'true' &&
    !element.closest('[aria-hidden="true"]') &&
    element.offsetParent !== null
  ));

  /* ------------------------------------------------------------------ */
  /* 1. Drawer / aside controller — deliberately simple and reliable    */
  /* ------------------------------------------------------------------ */
  let activeAside = null;
  let previousBodyOverflow = '';

  const getOverlay = (type) => document.querySelector(
    `.overlay[data-aside="${CSS.escape(type)}"]`
  );

  const getPanel = (overlay) => (
    overlay?.querySelector('[data-aside-panel]') ||
    overlay?.querySelector('aside') ||
    null
  );

  const setOverlayOpen = (overlay, open) => {
    if (!overlay) return;

    overlay.classList.toggle('expanded', open);
    overlay.setAttribute('aria-hidden', open ? 'false' : 'true');
  };

  const getInitialFocus = (overlay) => {
    if (!overlay) return null;

    if (overlay.getAttribute('data-aside') === 'search') {
      return overlay.querySelector('[data-predictive-input]');
    }

    const panel = getPanel(overlay);

    return (
      panel?.querySelector('[data-aside-initial-focus]') ||
      visibleFocusables(panel)[0] ||
      panel
    );
  };

  const closeAside = (overlay = activeAside?.overlay, options = {}) => {
    if (!overlay) return;

    const isActive = activeAside?.overlay === overlay;
    const trigger = isActive ? activeAside.trigger : null;
    const type = overlay.getAttribute('data-aside');

    setOverlayOpen(overlay, false);

    if (isActive) {
      document.body.style.overflow = previousBodyOverflow;
      document.body.removeAttribute('data-aside-open');
      activeAside = null;
    }

    document.dispatchEvent(new CustomEvent('aside:close', {
      detail: { type },
    }));

    if (
      isActive &&
      options.restoreFocus !== false &&
      trigger instanceof HTMLElement &&
      trigger.isConnected
    ) {
      window.requestAnimationFrame(() => {
        trigger.focus({ preventScroll: true });
      });
    }
  };

  const closeAllAsides = (options = {}) => {
    const activeOverlay = activeAside?.overlay || null;

    document.querySelectorAll('.overlay.expanded[data-aside]').forEach((overlay) => {
      closeAside(overlay, {
        restoreFocus: overlay === activeOverlay && options.restoreFocus !== false,
      });
    });
  };

  const openAside = (type, trigger) => {
    const overlay = getOverlay(type);
    if (!overlay) return;

    if (activeAside?.overlay === overlay) return;

    if (activeAside) {
      closeAside(activeAside.overlay, { restoreFocus: false });
    }

    document.dispatchEvent(new CustomEvent('mega:close-all'));

    previousBodyOverflow = document.body.style.overflow;
    activeAside = {
      overlay,
      trigger: trigger instanceof HTMLElement ? trigger : document.activeElement,
    };

    setOverlayOpen(overlay, true);
    document.body.style.overflow = 'hidden';
    document.body.setAttribute('data-aside-open', type);

    window.requestAnimationFrame(() => {
      getInitialFocus(overlay)?.focus({ preventScroll: true });
    });

    document.dispatchEvent(new CustomEvent('aside:open', {
      detail: { type },
    }));
  };

  // Establish a clean closed state. No inert or global aria-hidden mutations.
  document.querySelectorAll('.overlay[data-aside]').forEach((overlay) => {
    setOverlayOpen(overlay, false);

    // Direct listeners make the close controls independent from shared state
    // and from document-level event delegation.
    overlay.querySelectorAll('[data-aside-close]').forEach((closer) => {
      // Real navigation links must remain completely native. The destination
      // page naturally replaces the open drawer.
      if (closer instanceof HTMLAnchorElement && closer.hasAttribute('href')) {
        return;
      }

      closer.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        closeAside(overlay);
      });
    });
  });

  document.querySelectorAll('[data-aside-open]').forEach((opener) => {
    opener.addEventListener('click', (event) => {
      const type = opener.getAttribute('data-aside-open');
      if (!type) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      openAside(type, opener);
    });
  });

  document.addEventListener('keydown', (event) => {
    if (!activeAside) return;

    const { overlay } = activeAside;
    const panel = getPanel(overlay);

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();
      closeAside(overlay);
      return;
    }

    if (event.key !== 'Tab' || !panel) return;

    const focusables = visibleFocusables(panel);

    if (!focusables.length) {
      event.preventDefault();
      panel.focus({ preventScroll: true });
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  }, true);

  window.ZaynaAside = {
    open: openAside,
    close: () => closeAside(),
    closeAll: closeAllAsides,
  };

  /* ------------------------------------------------------------------ */
  /* 2. Desktop mega menu — isolated from drawer state                  */
  /* ------------------------------------------------------------------ */
  const megaItems = Array.from(document.querySelectorAll('[data-mega-item]'));
  const primaryControls = Array.from(document.querySelectorAll('[data-nav-primary]'));
  let openMegaItem = null;
  let pointerCloseTimer = 0;

  const panelLinks = (item) => visibleFocusables(
    item?.querySelector('[data-mega-panel]')
  );

  const setMegaPanelState = (panel, open) => {
    if (!panel) return;
    panel.setAttribute('aria-hidden', open ? 'false' : 'true');
  };

  const closeMega = (item = openMegaItem, options = {}) => {
    if (!item) return;

    const trigger = item.querySelector('[data-mega-trigger]');
    const panel = item.querySelector('[data-mega-panel]');

    item.classList.remove('is-open');
    trigger?.setAttribute('aria-expanded', 'false');
    setMegaPanelState(panel, false);

    if (openMegaItem === item) openMegaItem = null;

    if (options.restoreFocus && trigger) {
      trigger.focus({ preventScroll: true });
    }
  };

  const closeAllMega = () => {
    megaItems.forEach((item) => closeMega(item));
  };

  const openMega = (item, options = {}) => {
    if (!item || window.matchMedia('(max-width: 767px)').matches) return;

    window.clearTimeout(pointerCloseTimer);

    megaItems.forEach((otherItem) => {
      if (otherItem !== item) closeMega(otherItem);
    });

    const trigger = item.querySelector('[data-mega-trigger]');
    const panel = item.querySelector('[data-mega-panel]');
    if (!trigger || !panel) return;

    item.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    setMegaPanelState(panel, true);
    openMegaItem = item;

    if (options.focusFirst) {
      panelLinks(item)[0]?.focus({ preventScroll: true });
    }
  };

  const movePrimaryFocus = (current, direction) => {
    if (!primaryControls.length) return;

    const currentIndex = Math.max(0, primaryControls.indexOf(current));
    let nextIndex = currentIndex;

    if (direction === 'first') nextIndex = 0;
    if (direction === 'last') nextIndex = primaryControls.length - 1;
    if (direction === 'next') nextIndex = (currentIndex + 1) % primaryControls.length;
    if (direction === 'previous') {
      nextIndex = (currentIndex - 1 + primaryControls.length) % primaryControls.length;
    }

    closeAllMega();
    primaryControls[nextIndex]?.focus({ preventScroll: true });
  };

  megaItems.forEach((item) => {
    const trigger = item.querySelector('[data-mega-trigger]');
    const panel = item.querySelector('[data-mega-panel]');
    if (!trigger || !panel) return;

    setMegaPanelState(panel, false);

    trigger.addEventListener('click', (event) => {
      event.preventDefault();

      if (item.classList.contains('is-open')) closeMega(item);
      else openMega(item);
    });

    trigger.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        openMega(item, { focusFirst: true });
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        movePrimaryFocus(trigger, 'next');
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        movePrimaryFocus(trigger, 'previous');
      } else if (event.key === 'Home') {
        event.preventDefault();
        movePrimaryFocus(trigger, 'first');
      } else if (event.key === 'End') {
        event.preventDefault();
        movePrimaryFocus(trigger, 'last');
      } else if (event.key === 'Escape') {
        event.preventDefault();
        closeMega(item, { restoreFocus: true });
      }
    });

    item.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'touch') openMega(item);
    });

    item.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'touch') return;

      window.clearTimeout(pointerCloseTimer);
      pointerCloseTimer = window.setTimeout(() => {
        if (!item.contains(document.activeElement)) closeMega(item);
      }, 140);
    });

    item.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (!item.contains(document.activeElement)) closeMega(item);
      }, 0);
    });

    panel.addEventListener('keydown', (event) => {
      const links = panelLinks(item);
      const currentIndex = links.indexOf(document.activeElement);

      if (event.key === 'Escape') {
        event.preventDefault();
        closeMega(item, { restoreFocus: true });
        return;
      }

      if (!links.length || currentIndex < 0) return;

      let nextIndex = currentIndex;

      if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % links.length;
      else if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + links.length) % links.length;
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = links.length - 1;
      else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        closeMega(item, { restoreFocus: true });
        return;
      } else return;

      event.preventDefault();
      links[nextIndex]?.focus({ preventScroll: true });
    });
  });

  primaryControls.forEach((control) => {
    if (control.matches('[data-mega-trigger]')) return;

    control.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        movePrimaryFocus(control, 'next');
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        movePrimaryFocus(control, 'previous');
      } else if (event.key === 'Home') {
        event.preventDefault();
        movePrimaryFocus(control, 'first');
      } else if (event.key === 'End') {
        event.preventDefault();
        movePrimaryFocus(control, 'last');
      }
    });
  });

  document.addEventListener('click', (event) => {
    if (openMegaItem && !openMegaItem.contains(event.target)) {
      closeMega(openMegaItem);
    }
  });

  document.addEventListener('mega:close-all', closeAllMega);

  /* ------------------------------------------------------------------ */
  /* 3. Curated carousel                                                */
  /* ------------------------------------------------------------------ */
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.querySelectorAll('[data-carousel]').forEach((root) => {
    const track = root.querySelector('[data-carousel-track]');
    if (!track) return;

    const step = () => Math.min(track.clientWidth * 0.8, 380);
    const prev = root.querySelector('[data-carousel-prev]');
    const next = root.querySelector('[data-carousel-next]');
    const behavior = reduceMotion ? 'auto' : 'smooth';

    if (!track.hasAttribute('tabindex')) track.tabIndex = 0;
    track.setAttribute('role', 'region');
    track.setAttribute('aria-label', track.getAttribute('aria-label') || 'Productcarrousel');

    prev?.addEventListener('click', () => {
      track.scrollBy({ left: -step(), behavior });
    });

    next?.addEventListener('click', () => {
      track.scrollBy({ left: step(), behavior });
    });

    track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        track.scrollBy({ left: step(), behavior });
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        track.scrollBy({ left: -step(), behavior });
      } else if (event.key === 'Home') {
        event.preventDefault();
        track.scrollTo({ left: 0, behavior });
      } else if (event.key === 'End') {
        event.preventDefault();
        track.scrollTo({ left: track.scrollWidth, behavior });
      }
    });

    if (reduceMotion || track.dataset.autoscroll !== 'true') return;

    let paused = false;
    let position = track.scrollLeft;

    const resync = () => {
      position = track.scrollLeft;
    };

    root.addEventListener('pointerenter', () => { paused = true; });
    root.addEventListener('pointerleave', () => { paused = false; resync(); });
    root.addEventListener('focusin', () => { paused = true; });
    root.addEventListener('focusout', () => { paused = false; resync(); });

    const loopDistance = () => {
      const items = track.children;
      const count = items.length / 2;

      if (count < 1 || !items[count]) return track.scrollWidth / 2;
      return items[count].offsetLeft - items[0].offsetLeft;
    };

    const speed = 0.8;

    const tick = () => {
      if (!paused) {
        const loop = loopDistance();
        position += speed;

        if (loop > 0 && position >= loop) position -= loop;
        track.scrollLeft = position;
      }

      window.requestAnimationFrame(tick);
    };

    window.requestAnimationFrame(tick);
  });
})();
